import React, { FC, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, CardTitle } from 'reactstrap';
import { EmailOverrideInfo } from '../../../../../shared/email';
import { ActionButtonModal, MaterialIcon } from '../../../../components';
import { useTranslation } from '../../../../translations';

interface TemplateBodyEntryProps {
  readonly body: {
    raw: string;
    rendered: string;
  };
  readonly active?: boolean;
}

export const DefaultTemplateCard: FC<TemplateBodyEntryProps> = ({ body, active }) => {
  const { ts } = useTranslation();
  return (
    <Card className="mb-10">
      <CardBody>
        <CardTitle className="align-middle">
          {ts('default_template')}{' '}
          <span className="align-middle badge-list compact">
            {active && <Badge color="success">{ts('current')}</Badge>}
          </span>
        </CardTitle>
        <hr />
        <RenderContainer data={body.rendered} />
      </CardBody>
    </Card>
  );
};

export const TemplateOverrideCard: FC<{
  readonly level: 'convention' | 'organization';
  readonly templateName: string;
  readonly override?: EmailOverrideInfo;
  readonly active?: boolean;
  readonly enableModify: () => void;
  readonly setIsCreating: (value: boolean) => void;
  readonly refresh: () => void;
}> = ({ level, override, templateName, active, enableModify, setIsCreating, refresh }) => {
  const { ts } = useTranslation();
  return (
    <Card className="mb-10">
      <CardBody>
        <CardTitle className="align-middle">
          <span className="text-capitalize">{ts('level_level_override', level)}</span>{' '}
          <span className="align-middle badge-list compact">
            {active && <Badge color="success">{ts('current')}</Badge>}
          </span>
          <div className="float-end">
            {!override && (
              <Button
                color="primary"
                onClick={() => {
                  setIsCreating(true);
                  enableModify();
                }}
              >
                {ts('create_override')}
              </Button>
            )}
            {override && override.disabled && (
              <ActionButtonModal
                actionContent="reactivate_template"
                buttonContent="reactivate_template"
                color="success"
                id="reactivate"
                onComplete={async () => {
                  await api.deleteOverride(templateName, level);

                  refresh();
                  toast.success(ts('reactivation_complete'));
                }}
                title="reactivate_email_template"
              >
                Are you sure you want to reactivate "{templateName}"? This {level} will be
                configured to send this email again.
                <br />
                <br />
                {level === 'organization' && (
                  <p>
                    <MaterialIcon name="warning" />
                    {ts('if_this_template_contains_an')}
                  </p>
                )}
              </ActionButtonModal>
            )}
            {override && !override.disabled && (
              <>
                <Button
                  color="primary"
                  onClick={() => {
                    setIsCreating(false);
                    enableModify();
                  }}
                >
                  <MaterialIcon name="edit" />
                </Button>{' '}
                <ActionButtonModal
                  actionContent="common.delete"
                  buttonContent={<MaterialIcon name="delete" />}
                  color="danger"
                  id="delete"
                  onComplete={async () => {
                    await api.deleteOverride(templateName, level);

                    refresh();
                    toast.success(ts('override_has_been_deleted'));
                  }}
                  title="delete_override"
                >
                  {ts('are_you_sure_delete_override')}
                </ActionButtonModal>
              </>
            )}
          </div>
        </CardTitle>
        <hr />
        {!override && (
          <div className="no-override-notice">{ts('no_level_level_override_exists', level)}</div>
        )}
        {override && override.disabled && (
          <Card className="danger">
            <CardBody className="text-center">
              <CardTitle style={{ marginBottom: 0 }}>
                {ts('this_email_will_not_be', level)}
              </CardTitle>
            </CardBody>
          </Card>
        )}
        {override && !override.disabled && <RenderContainer data={override.body.rendered} />}
      </CardBody>
    </Card>
  );
};

export const RenderContainer: FC<{ readonly data: string }> = ({ data }) => {
  const frameRef = useRef<HTMLIFrameElement>(null);

  const getLineHeight = useCallback(() => {
    if (frameRef.current?.contentDocument) {
      const frameHeight = frameRef.current.contentDocument.body.clientHeight;
      frameRef.current.style.height = `${frameHeight + 45}px`;
    }
  }, []);

  return (
    <iframe
      className="rounded w-100"
      onLoad={getLineHeight}
      ref={frameRef}
      referrerPolicy="same-origin"
      sandbox="allow-same-origin"
      srcDoc={data}
      style={{ border: '1px solid rgba(0, 0, 0, .125)' }}
    />
  );
};
